import { FiMenu, FiMoon, FiSun, FiX } from 'react-icons/fi';
import useThemeSwitcher from '../../hooks/useThemeSwitcher';
import HireMeModal from '../HireMeModal';
import logoLight from '../../images/logo-light.svg';
import logoDark from '../../images/logo-dark.svg';
import { motion } from 'framer-motion';
import Button from '../reusable/Button';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const AppHeader = () => {
	const [showMenu, setShowMenu] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [activeTheme, setTheme] = useThemeSwitcher();

	const location = useLocation();

	function toggleMenu() {
		setShowMenu((prev) => !prev);
	}

	function showHireMeModal() {
		if (!showModal) {
			document.getElementsByTagName('html')[0].classList.add('overflow-y-hidden');
			setShowModal(true);
		} else {
			document.getElementsByTagName('html')[0].classList.remove('overflow-y-hidden');
			setShowModal(false);
		}
	}

	const [activeSection, setActiveSection] = useState('home');

	useEffect(() => {
		// Only track active sections on the homepage (because only there the IDs exist)
		if (location.pathname !== '/') return;

		const ids = ['home', 'projects', 'about', 'contact'];
		const sections = ids.map((id) => document.getElementById(id)).filter(Boolean);

		if (!sections.length) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const visible = entries
					.filter((e) => e.isIntersecting)
					.sort((a, b) => (b.intersectionRatio ?? 0) - (a.intersectionRatio ?? 0))[0];

				if (visible?.target?.id) setActiveSection(visible.target.id);
			},
			{ root: null, threshold: 0.3 }
		);

		sections.forEach((s) => observer.observe(s));
		return () => observer.disconnect();
	}, [location.pathname]);

	const navClass = (id) =>
		`block text-left text-lg text-primary-dark dark:text-ternary-light hover:text-secondary-dark dark:hover:text-secondary-light sm:mx-4 mb-2 sm:py-2 relative
	 ${location.pathname === '/' && activeSection === id
			? 'after:content-[""] after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-full after:bg-indigo-500'
			: ''}`;

	return (
		<motion.nav
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			id="nav"
			className="sm:container sm:mx-auto"
		>
			<div className="z-10 max-w-screen-lg xl:max-w-screen-xl block sm:flex sm:justify-between sm:items-center py-6">
				{/* Header menu links and small screen hamburger menu */}
				<div className="flex justify-between items-center px-4 sm:px-0">
					<div>
						{/* ✅ Works from any route */}
						<Link to="/#home" aria-label="Home">
							{activeTheme === 'dark' ? (
								<img src={logoDark} className="w-36" alt="Dark Logo" />
							) : (
								<img src={logoLight} className="w-36" alt="Light Logo" />
							)}
						</Link>
					</div>

					{/* Theme switcher small screen */}
					<div
						onClick={() => setTheme(activeTheme)}
						aria-label="Theme Switcher"
						className="block sm:hidden ml-0 bg-primary-light dark:bg-ternary-dark p-3 shadow-sm rounded-xl cursor-pointer"
					>
						{activeTheme === 'dark' ? (
							<FiMoon className="text-ternary-dark hover:text-gray-400 dark:text-ternary-light dark:hover:text-primary-light text-xl" />
						) : (
							<FiSun className="text-gray-200 hover:text-gray-50 text-xl" />
						)}
					</div>

					{/* Small screen hamburger menu */}
					<div className="sm:hidden">
						<button
							onClick={toggleMenu}
							type="button"
							className="focus:outline-none"
							aria-label="Hamburger Menu"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 24 24"
								className="h-7 w-7 fill-current text-secondary-dark dark:text-ternary-light"
							>
								{showMenu ? <FiX className="text-3xl" /> : <FiMenu className="text-3xl" />}
							</svg>
						</button>
					</div>
				</div>

				{/* Header links small screen */}
				<div
					className={
						showMenu
							? 'block m-0 sm:ml-4 mt-5 sm:mt-3 sm:flex p-5 sm:p-0 justify-center items-center shadow-lg sm:shadow-none'
							: 'hidden'
					}
				>
					<Link
						to="/#projects"
						className={navClass('projects')}
						aria-label="Projects"
						onClick={() => setShowMenu(false)}
					>
						Projects
					</Link>

					<Link
						to="/#about"
						className={navClass('about') + ' border-t-2 pt-3 sm:pt-2 sm:border-t-0 border-primary-light dark:border-secondary-dark'}
						aria-label="About Me"
						onClick={() => setShowMenu(false)}
					>
						About Me
					</Link>

					<Link
						to="/#contact"
						className={navClass('contact') + ' border-t-2 pt-3 sm:pt-2 sm:border-t-0 border-primary-light dark:border-secondary-dark'}
						aria-label="Contact"
						onClick={() => setShowMenu(false)}
					>
						Contact
					</Link>

					<div className="border-t-2 pt-3 sm:pt-0 sm:border-t-0 border-primary-light dark:border-secondary-dark">
						<span
							onClick={showHireMeModal}
							className="font-general-medium sm:hidden block text-left text-md bg-indigo-500 hover:bg-indigo-600 text-white shadow-sm rounded-sm px-4 py-2 mt-2 duration-300 w-24"
							aria-label="Hire Me Button"
						>
							<Button title="Hire Me" />
						</span>
					</div>
				</div>

				{/* Header links large screen */}
				<div className="font-general-medium hidden m-0 sm:ml-4 mt-5 sm:mt-3 sm:flex p-5 sm:p-0 justify-center items-center shadow-lg sm:shadow-none">
					<Link to="/#projects" className={navClass('projects')} aria-label="Projects">
						Projects
					</Link>

					<Link to="/#about" className={navClass('about')} aria-label="About Me">
						About Me
					</Link>

					<Link to="/#contact" className={navClass('contact')} aria-label="Contact">
						Contact
					</Link>
				</div>

				{/* Header right section buttons */}
				<div className="hidden sm:flex justify-between items-center flex-col md:flex-row">
					<div className="hidden md:flex">
						<span
							onClick={showHireMeModal}
							className="text-md font-general-medium bg-indigo-500 hover:bg-indigo-600 text-white shadow-sm rounded-md px-5 py-2.5 duration-300"
							aria-label="Hire Me Button"
						>
							<Button title="Hire Me" />
						</span>
					</div>

					{/* Theme switcher large screen */}
					<div
						onClick={() => setTheme(activeTheme)}
						aria-label="Theme Switcher"
						className="ml-8 bg-primary-light dark:bg-ternary-dark p-3 shadow-sm rounded-xl cursor-pointer"
					>
						{activeTheme === 'dark' ? (
							<FiMoon className="text-ternary-dark hover:text-gray-400 dark:text-ternary-light dark:hover:text-primary-light text-xl" />
						) : (
							<FiSun className="text-gray-200 hover:text-gray-50 text-xl" />
						)}
					</div>
				</div>
			</div>

			{/* Hire me modal */}
			<div>
				{showModal ? (
					<HireMeModal onClose={showHireMeModal} onRequest={showHireMeModal} />
				) : null}
				{showModal ? showHireMeModal : null}
			</div>
		</motion.nav>
	);
};

export default AppHeader;
